import {
	AutoAwesome,
	Close,
	FileDownloadOutlined,
	Fullscreen,
	FullscreenExit,
	InfoOutlined,
	ZoomIn,
	ZoomOut,
} from '@mui/icons-material'
import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { useMediaQuery } from 'react-responsive'
import AppContext from '../../../../context/appContext'
import { useClickOutside } from '@mantine/hooks'
import {
	Button,
	IconButton,
	OptionsMenuButton,
	Select,
	Tooltip,
} from '../../../../components/ui'
import cn from '../../../../lib/utils/cn'
import {
	CurrentDocumentData,
	DocumentData,
} from '../../../../lib/api/types/document.interfaces'
import FileFormatIcon from '../../../../components/common/FileFormatIcon'
import { Features, useBilling } from '../../../../context/billingContext'
import {
	useDownloadFile,
	useDownloadSharedFile,
} from '../../../../hooks/api/documentHooks'
import { ReactComponent as SigningSymbol } from '../../../../assets/icons/interactions/signing-symbol.svg'
import CreateESignObjectModal from '../../../../components/modals/workspace/CreateESignObjectModal'
import { useSigningFlow } from '../../../../hooks/file/useSigningFlow'
import { useAuth as useAuraAuth } from '../../../../hooks'
import { isFileNameEsignSupported } from '../../../../lib/utils/esign'

interface PreviewHeaderProps {
	isChatOpened?: boolean
	onOpenChat?: () => void
	onClose?: () => void
	onFileInfo?: () => void
	currentDocument?: CurrentDocumentData | DocumentData
	versionId?: string
	isLoading?: boolean
	isNotSupported?: boolean
	isModal?: boolean
	isApp?: boolean
	isPublicPreview?: boolean
	sharedToken?: string
	isUserInSharedList?: boolean
	url?: string
	shouldOpenSignModal?: boolean
}

const SCALES = [
	{ label: '10%', value: 10 },
	{ label: '20%', value: 20 },
	{ label: '30%', value: 30 },
	{ label: '50%', value: 50 },
	{ label: '75%', value: 75 },
	{ label: '100%', value: 100 },
	{ label: '125%', value: 125 },
	{ label: '150%', value: 150 },
	{ label: '175%', value: 175 },
	{ label: 'Fit', value: 'fit' },
]

const PreviewHeader: FC<PreviewHeaderProps> = ({
	isChatOpened = false,
	onClose,
	onOpenChat,
	onFileInfo,
	currentDocument,
	versionId,
	isLoading,
	isNotSupported = false,
	isModal = false,
	isApp = false,
	isPublicPreview = false,
	sharedToken,
	isUserInSharedList = false,
	url,
	shouldOpenSignModal = false,
}) => {
	const navigate = useNavigate()
	const { hasFeature } = useBilling()
	const { login, logout, isAuthenticated } = useAuraAuth()

	const { control } = useFormContext()

	const { mutateAsync: downloadFile, isPending: isDownloadingFile } =
		useDownloadFile()
	const { mutateAsync: downloadSharedFile, isPending: isDownloadingShared } =
		useDownloadSharedFile()
	const {
		isCreateDraftModalOpen,
		createDraftParams,
		onSendToESign,
		closeCreateDraftModal,
		createESignDraft,
		isCreateESignObjectPending,
	} = useSigningFlow({
		documentIdFromUrl: currentDocument?.id
			? String(currentDocument.id)
			: undefined,
	})

	const isPreparingToDownload = isPublicPreview
		? isDownloadingShared
		: isDownloadingFile
	const isMedium = useMediaQuery({ maxWidth: 1430 })
	const isMobile = useMediaQuery({ maxWidth: 768 })
	const [isLoginPromptOpen, setIsLoginPromptOpen] = useState(false)
	const loginPromptRef = useClickOutside(() => setIsLoginPromptOpen(false))

	const resolvedName = useMemo(() => {
		const baseName = currentDocument?.name
		if (!versionId) return baseName

		const docAny = currentDocument as any
		const versions = docAny?.versions
		if (!Array.isArray(versions)) return baseName

		return versions.find((v: any) => v?.id === versionId)?.name ?? baseName
	}, [currentDocument, versionId])

	const resolvedVersionId = useMemo(() => {
		if (versionId) return versionId
		if (currentDocument?.versionId) return currentDocument.versionId

		const docAny = currentDocument as any
		const versions = Array.isArray(docAny?.versions) ? docAny.versions : []
		return versions.find((version: any) => version?.currentVersion)?.id ?? null
	}, [currentDocument, versionId])

	const canSignWithEsign = useMemo(
		() => isFileNameEsignSupported(resolvedName),
		[resolvedName],
	)

	const hasOpenedSignModalRef = useRef(false)

	useEffect(() => {
		if (!shouldOpenSignModal) {
			hasOpenedSignModalRef.current = false
			return
		}

		if (
			hasOpenedSignModalRef.current ||
			!currentDocument?.id ||
			!canSignWithEsign
		) {
			return
		}

		hasOpenedSignModalRef.current = true
		onSendToESign({
			fileId: String(currentDocument.id),
			versionId: resolvedVersionId,
		})
	}, [
		shouldOpenSignModal,
		currentDocument?.id,
		canSignWithEsign,
		resolvedVersionId,
		onSendToESign,
	])

	const handleDownload = async () => {
		if (isPublicPreview) {
			if (!sharedToken || !isAuthenticated || !isUserInSharedList) {
				setIsLoginPromptOpen(true)
				return
			}
			await downloadSharedFile({
				sharedToken,
				fileName: resolvedName,
			})
			return
		}

		await downloadFile({
			id: currentDocument?.id,
			versionId,
			fileName: resolvedName,
			fileType: currentDocument?.fileType,
		})
	}

	return (
		<>
			<div
				className={cn(
					'bg-white dark:bg-background-dark  flex items-center justify-between w-auto flex-1 border-b border-stroke-dark dark:border-stroke-dark-dark max-h-20 px-2.5 md:px-5 z-10 box-border',
					isChatOpened && 'lg:justify-between',
				)}
			>
				{!isModal && !isPublicPreview && (
					<div
						className={cn(
							'hidden md:flex w-1/3',
							isChatOpened && 'opacity-0 lg:w-1/3 md:hidden',
						)}
					>
						{hasFeature(Features.Qa) && !!onOpenChat && (
							<Button
								label="Chat"
								variant="tertiary"
								onClick={onOpenChat}
								leftIcon={<AutoAwesome className="w-5" />}
								className={cn(
									'text-white bg-gradient-to-b from-background-gradient-start to-background-gradient-end w-10 h-10 p-0  items-center justify-center md:w-auto md:px-3.5 hidden md:flex',
									isChatOpened && 'opacity-0 pointer-events-none',
								)}
								classNameLabel="hidden md:block"
								disabled={isNotSupported}
							/>
						)}
					</div>
				)}

				<h2 className="text-sm md:text-lg text-black dark:text-text-main-dark flex-grow text-center md:text-left overflow-hidden">
					{isLoading ? (
						<p className="flex items-center gap-2 overflow-hidden justify-start lg:justify-center">
							Loading...
						</p>
					) : resolvedName ? (
						<div className="flex items-center gap-2 overflow-hidden justify-start lg:justify-center">
							<FileFormatIcon fileName={resolvedName} />
							<span className="truncate block">{resolvedName}</span>
						</div>
					) : (
						'Document Preview'
					)}
				</h2>

				<div
					className={cn(
						'flex items-center justify-end gap-2 w-auto md:w-1/3',
						isModal && 'md:w-2/3',
						isChatOpened && 'md:w-auto md:min-w-fit xl:w-1/3',
					)}
				>
					{!isMobile && (
						<Controller
							control={control}
							name="scale"
							render={({ field: { value, onChange } }) => {
								const numericScales = SCALES.filter(
									(s) => typeof s.value === 'number',
								) as { value: number; label: string }[]

								const isFit = value === 'fit'

								const handleZoomIn = () => {
									if (isFit) {
										onChange(isMedium ? 75 : 100)
										return
									}
									const next = numericScales.find(
										(s) => s.value > (value as number),
									)
									onChange(next?.value ?? value)
								}

								const handleZoomOut = () => {
									if (isFit) {
										onChange(isMedium ? 20 : 50)
										return
									}
									const prev = [...numericScales]
										.reverse()
										.find((s) => s.value < (value as number))
									onChange(prev?.value ?? value)
								}

								return (
									<div className="flex items-center justify-center gap-2">
										<IconButton
											icon={<ZoomOut className="dark:fill-text-main-dark" />}
											onClick={handleZoomOut}
											variant="secondary"
											disabled={
												(!isFit && value === numericScales[0].value) ||
												isNotSupported
											}
											tooltipPosition="bottom"
											tooltip={'Zoom out'}
										/>
										<IconButton
											icon={<ZoomIn className="dark:fill-text-main-dark" />}
											onClick={handleZoomIn}
											variant="secondary"
											disabled={
												(!isFit &&
													value ===
														numericScales[numericScales.length - 1].value) ||
												isNotSupported
											}
											tooltipPosition="bottom"
											tooltip={'Zoom in'}
										/>
										<Select
											wrapperClassName="hidden lg:block"
											className="h-10"
											options={SCALES}
											value={value}
											onChange={onChange}
											disabled={isNotSupported}
										/>
										<IconButton
											variant="secondary"
											icon={
												isFit ? (
													<FullscreenExit className="w-5 h-5 dark:fill-text-main-dark" />
												) : (
													<Fullscreen className="w-5 h-5 dark:fill-text-main-dark" />
												)
											}
											onClick={() => {
												onChange(isFit ? 20 : 'fit')
											}}
											tooltipPosition="bottom"
											tooltip={isFit ? 'Show all' : 'Fit to width'}
											disabled={isNotSupported}
										/>
										{url && !isPublicPreview && !isApp && (
											<Button
												variant="secondary"
												label="Sign"
												leftIcon={
													<SigningSymbol className="w-3.5 h-3.5 text-text-secondary" />
												}
												onClick={() => {
													if (!currentDocument?.id) return
													onSendToESign({
														fileId: String(currentDocument.id),
														versionId: resolvedVersionId,
													})
												}}
												classNameLabel=""
												classNameContent="gap-2.5"
												disabled={isNotSupported || !canSignWithEsign}
												tooltip={
													!canSignWithEsign
														? 'Signing is only available for PDF, DOC, and DOCX files.'
														: null
												}
												tooltipOnDisabled
												tooltipPosition="bottom"
											/>
										)}
									</div>
								)
							}}
						/>
					)}

					{!isPublicPreview && (
						<OptionsMenuButton
							disabled={isNotSupported}
							className="md:hidden"
							buttonClassName="w-10 h-10"
							defaultPosition={{
								top: 20,
							}}
							items={[
								{
									icon: (
										<AutoAwesome className="w-5 h-5 dark:fill-text-main-dark" />
									),
									label: 'Chat',
									onClick: onOpenChat,
								},
								{
									icon: (
										<InfoOutlined className="w-5 h-5 dark:fill-text-main-dark" />
									),
									label: 'File info',
									onClick: onFileInfo,
									visible: !!onFileInfo,
								},
								{
									icon: (
										<FileDownloadOutlined className="w-5 h-5 dark:fill-text-main-dark" />
									),
									label: 'Download',
									onClick: handleDownload,
									visible: !isApp,
								},
							]}
						/>
					)}

					{!isPublicPreview && (
						<IconButton
							variant="secondary"
							icon={
								<InfoOutlined className="w-5 h-5 dark:fill-text-main-dark" />
							}
							onClick={onFileInfo}
							className="hidden md:block min-w-10"
							tooltipPosition="bottom"
							tooltip={'File info'}
							disabled={isNotSupported}
						/>
					)}

					{!isApp && (
						<div className="relative hidden md:block" ref={loginPromptRef}>
							<IconButton
								variant="secondary"
								icon={
									<FileDownloadOutlined className="w-5 h-5 dark:fill-text-main-dark" />
								}
								onClick={handleDownload}
								className="min-w-10"
								tooltipPosition="bottom"
								tooltip="Download"
								disabled={!isPublicPreview && isNotSupported}
								loading={isPreparingToDownload}
							/>
							{isPublicPreview &&
								isLoginPromptOpen &&
								(!isAuthenticated || !isUserInSharedList) && (
									<div
										className={cn(
											'absolute right-0 top-full mt-1 z-[1001] min-w-[250px]',
											'bg-white dark:bg-background-dark default-shadow rounded-lg',
											'border border-stroke-light dark:border-stroke-light-dark overflow-hidden',
											'p-3',
										)}
									>
										<p className="text-sm text-text-main dark:text-text-main-dark mb-3">
											{!isAuthenticated
												? 'You need to log in to download this file.'
												: 'Your account is not in the shared list. Switch account to download.'}
										</p>
										<Button
											variant="primary"
											label={!isAuthenticated ? 'Log in' : 'Switch account'}
											onClick={() => {
												setIsLoginPromptOpen(false)
												if (!isAuthenticated) {
													login()
												} else {
													logout()
												}
											}}
											className="w-full"
										/>
									</div>
								)}
						</div>
					)}

					{!isPublicPreview && !!onClose && (
						<IconButton
							variant="secondary"
							icon={<Close className="w-5 h-5 dark:fill-text-main-dark" />}
							onClick={onClose}
							tooltip={'Close'}
							className="min-w-10"
						/>
					)}
				</div>
			</div>
			<CreateESignObjectModal
				isOpen={isCreateDraftModalOpen}
				onClose={closeCreateDraftModal}
				title="Create eSign Document"
				fileId={createDraftParams?.fileId}
				initialVersionId={createDraftParams?.versionId ?? null}
				inputPlaceholder="Aura June Invoice"
				icon={<SigningSymbol />}
				loading={isCreateESignObjectPending}
				onAction={async (name: string, versionId: string) => {
					const created = await createESignDraft(name, versionId)
					if (!created) return

					const searchParams = new URLSearchParams()
					searchParams.set('documentId', created.documentId)
					searchParams.set('eSignObjectId', created.eSignObjectId)
					if (created.versionId) {
						searchParams.set('versionId', created.versionId)
					}
					navigate(`/signing?${searchParams.toString()}`)
				}}
			/>
		</>
	)
}

export default PreviewHeader
